import { useState, useEffect } from 'react';
import { View, Text, Pressable, StyleSheet, Platform, ActivityIndicator, Modal, Image, TouchableWithoutFeedback } from 'react-native';
import { useNavigate } from 'react-router-dom';
import { Wrench, ChevronRight, Plus, MapPin, Calendar, AlertCircle, UserCheck, MessageSquare, Clock, DollarSign, Paperclip, Download, Eye, File, Image as ImageIcon, Video, FileText } from '../../lib/icons';
import { supabase } from '../../lib/supabase';
import { getAccessibleHomeIds } from '../../lib/homeMembership';
import { shadows } from '../../lib/styles';
import BottomSheet from '../../components/ui/BottomSheet';
import { downloadFile } from '../../lib/fileDownload';

interface ServiceRequest {
  id: string;
  title: string;
  description: string;
  priority: 'low' | 'normal' | 'high' | 'urgent';
  status: 'pending' | 'in_progress' | 'completed' | 'cancelled';
  created_at: string;
  home_id: string | null;
  visit_id?: string | null;
  visit_scheduled_at?: string | null;
  visit_completed_at?: string | null;
  owner_id?: string | null;
  home?: {
    address: string;
    city: string;
    state: string;
  };
  assigned_owner?: {
    id: string;
    full_name: string;
  } | null;
  workflow?: {
    current_stage: string;
    workflow_data: {
      triage: {
        status: string;
      };
    };
  } | null;
}

interface ServiceRequestNote {
  id: string;
  content: string;
  created_at: string;
  created_by: string;
  creator?: {
    full_name: string;
  } | null;
}

interface ServiceRequestQuote {
  id: string;
  service_request_id: string;
  service_provider_id?: string | null;
  amount?: number | null;
  notes?: string | null;
  status: 'pending' | 'accepted' | 'rejected';
  quote_date?: string | null;
  expires_at?: string | null;
  created_at: string;
  service_provider?: {
    id: string;
    company_name: string;
    email?: string;
    phone?: string;
    address?: string;
  } | null;
}

interface Attachment {
  id: string;
  file_url: string;
  file_name: string;
  file_size: number;
  file_type: string;
  created_at: string;
}

const ServiceRequestHistory = () => {
  const navigate = useNavigate();
  const [requests, setRequests] = useState<ServiceRequest[]>([]);
  const [loading, setLoading] = useState(true);
  const [selectedRequest, setSelectedRequest] = useState<ServiceRequest | null>(null);
  const [isDetailOpen, setIsDetailOpen] = useState(false);
  const [selectedRequestNotes, setSelectedRequestNotes] = useState<ServiceRequestNote[]>([]);
  const [loadingNotes, setLoadingNotes] = useState(false);
  const [quotes, setQuotes] = useState<ServiceRequestQuote[]>([]);
  const [loadingQuotes, setLoadingQuotes] = useState(false);
  const [quoteAttachments, setQuoteAttachments] = useState<Record<string, Attachment[]>>({});
  const [serviceRequestAttachments, setServiceRequestAttachments] = useState<Attachment[]>([]);
  const [loadingAttachments, setLoadingAttachments] = useState(false);
  const [previewImage, setPreviewImage] = useState<string | null>(null);

  useEffect(() => {
    fetchRequests();
  }, []);

  const fetchRequests = async () => {
    try {
      const { data: { user } } = await supabase.auth.getUser();
      
      if (!user) {
        setLoading(false);
        return;
      }

      const homeIds = await getAccessibleHomeIds(user.id);
      if (homeIds.length === 0) {
        setLoading(false);
        return;
      }

      const { data, error } = await supabase
        .from('service_requests')
        .select(`
          *,
          home:homes (
            address,
            city,
            state
          ),
          assigned_owner:profiles!owner_id (
            id,
            full_name
          )
        `)
        .in('home_id', homeIds)
        .in('status', ['pending', 'in_progress'])
        .order('created_at', { ascending: false })
        .limit(5);

      if (error) throw error;

      // Transform the data to handle arrays
      const transformedData = (data || []).map((request: any) => ({
        ...request,
        home: Array.isArray(request.home) ? request.home[0] : request.home,
        assigned_owner: Array.isArray(request.assigned_owner) ? request.assigned_owner[0] : request.assigned_owner
      }));

      // Fetch workflow data for each request to check triage status
      const requestsWithWorkflow = await Promise.all(
        transformedData.map(async (request: any) => {
          try {
            const { data: workflowData, error: workflowError } = await supabase
              .from('service_request_agent_workflow')
              .select('current_stage, workflow_data')
              .eq('service_request_id', request.id)
              .maybeSingle();
            
            // If error or no data, just continue without workflow data
            if (workflowError) {
              console.warn(`Error fetching workflow for request ${request.id}:`, workflowError);
            }
            
            return {
              ...request,
              workflow: workflowData || null
            };
          } catch (err) {
            // If any error occurs, return request without workflow data
            console.warn(`Error fetching workflow for request ${request.id}:`, err);
            return {
              ...request,
              workflow: null
            };
          }
        })
      );

      setRequests(requestsWithWorkflow);
    } catch (error) {
      console.error('Error fetching requests:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchNotes = async (requestId: string) => {
    try {
      setLoadingNotes(true);
      const { data: notesData, error: notesError } = await supabase
        .from('service_request_notes')
        .select('*')
        .eq('service_request_id', requestId)
        .order('created_at', { ascending: false });

      if (notesError) throw notesError;

      if (!notesData || notesData.length === 0) {
        setSelectedRequestNotes([]);
        return;
      }

      // Get unique user IDs from notes
      const userIds = [...new Set(notesData.map(note => note.created_by).filter(Boolean))];

      // Fetch user information for all creators
      const { data: usersData, error: usersError } = await supabase
        .from('profiles')
        .select('id, full_name')
        .in('id', userIds);

      if (usersError) {
        console.error('Error fetching user info:', usersError);
        setSelectedRequestNotes(notesData);
        return;
      }

      // Create a map of user IDs to user info
      const usersMap = new Map(usersData?.map(user => [user.id, user]) || []);

      // Enrich notes with user information
      const enrichedNotes = notesData.map(note => ({
        ...note,
        creator: usersMap.get(note.created_by) || null
      }));

      setSelectedRequestNotes(enrichedNotes);
    } catch (error) {
      console.error('Error fetching notes:', error);
      setSelectedRequestNotes([]);
    } finally {
      setLoadingNotes(false);
    }
  };

  const fetchQuotes = async (requestId: string) => {
    try {
      setLoadingQuotes(true);
      const { data, error } = await supabase
        .from('service_request_quotes')
        .select(`
          *,
          service_provider:service_providers (
            id,
            company_name,
            email,
            phone,
            address
          )
        `)
        .eq('service_request_id', requestId)
        .order('created_at', { ascending: false });

      if (error) throw error;

      // Transform the data and fetch attachments
      const attachmentsMap: Record<string, Attachment[]> = {};
      const transformedQuotes = await Promise.all(
        (data || []).map(async (quote: any) => {
          // Fetch full attachment data for each quote
          const { data: attachmentsData, error: attachmentsError } = await supabase
            .from('service_request_quote_attachments')
            .select('*')
            .eq('quote_id', quote.id)
            .order('created_at', { ascending: false });

          const attachments = attachmentsError ? [] : (attachmentsData || []);
          attachmentsMap[quote.id] = attachments;

          return {
            ...quote,
            service_provider: Array.isArray(quote.service_provider) 
              ? quote.service_provider[0] 
              : quote.service_provider
          };
        })
      );

      setQuotes(transformedQuotes);
      setQuoteAttachments(attachmentsMap);
    } catch (error) {
      console.error('Error fetching quotes:', error);
      setQuotes([]);
    } finally {
      setLoadingQuotes(false);
    }
  };

  const fetchAttachments = async (requestId: string) => {
    try {
      setLoadingAttachments(true);
      const { data, error } = await supabase
        .from('service_request_attachments')
        .select('*')
        .eq('service_request_id', requestId)
        .order('created_at', { ascending: false });

      if (error) throw error;
      setServiceRequestAttachments(data || []);
    } catch (error) {
      console.error('Error fetching attachments:', error);
      setServiceRequestAttachments([]);
    } finally {
      setLoadingAttachments(false);
    }
  };

  const getQuoteStatusColor = (status: ServiceRequestQuote['status']) => {
    switch (status) {
      case 'accepted': return { text: '#16a34a', bg: '#dcfce7' };
      case 'rejected': return { text: '#dc2626', bg: '#fee2e2' };
      default: return { text: '#ca8a04', bg: '#fef9c3' };
    }
  };

  const formatCurrency = (amount: number | null | undefined) => {
    if (amount === null || amount === undefined) return 'N/A';
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD'
    }).format(amount);
  };

  const formatFileSize = (bytes: number): string => {
    if (bytes < 1024) return bytes + ' B';
    if (bytes < 1024 * 1024) return (bytes / 1024).toFixed(1) + ' KB';
    return (bytes / (1024 * 1024)).toFixed(1) + ' MB';
  };

  const getFileIcon = (fileType: string) => {
    if (fileType.startsWith('image/')) return <ImageIcon size={16} color="#6b7280" />;
    if (fileType.startsWith('video/')) return <Video size={16} color="#6b7280" />;
    if (fileType === 'application/pdf') return <FileText size={16} color="#6b7280" />;
    return <File size={16} color="#6b7280" />;
  };

  const isImageFile = (fileType: string) => fileType.startsWith('image/');

  const downloadAttachment = async (attachment: Attachment, bucket: 'service-request-attachments' | 'service-request-quote-attachments') => {
    const { data: { publicUrl } } = supabase.storage
      .from(bucket)
      .getPublicUrl(attachment.file_url);

    await downloadFile(publicUrl, attachment.file_name);
  };

  const viewImage = (attachment: Attachment, bucket: 'service-request-attachments' | 'service-request-quote-attachments') => {
    const { data: { publicUrl } } = supabase.storage
      .from(bucket)
      .getPublicUrl(attachment.file_url);
    setPreviewImage(publicUrl);
  };

  const getPriorityColor = (priority: ServiceRequest['priority']) => {
    switch (priority) {
      case 'urgent': return { text: '#dc2626', bg: '#fef2f2' };
      case 'high': return { text: '#ea580c', bg: '#fff7ed' };
      case 'normal': return { text: '#2563eb', bg: '#eff6ff' };
      case 'low': return { text: '#16a34a', bg: '#f0fdf4' };
      default: return { text: '#4b5563', bg: '#f9fafb' };
    }
  };

  const renderProgressTimeline = (request: ServiceRequest) => {
    const status = request.status;
    const workflow = request.workflow;
    const isCancelled = status === 'cancelled';
    
    // Check if triage is in progress
    const isAnalyzing = workflow?.current_stage === 'triage' && 
                       (workflow.workflow_data?.triage?.status === 'in_progress' || 
                        workflow.workflow_data?.triage?.status === 'awaiting_confirmation');
    
    // 5-step timeline: submitted -> analyzing -> pending -> in_progress -> completed
    const steps = ['submitted', 'analyzing', 'pending', 'in_progress', 'completed'];
    const stepLabels = ['Submitted', 'Analyzing', 'Pending', 'In Progress', 'Completed'];
    
    let currentStepIndex = 0; // Default to submitted
    
    if (isCancelled) {
      currentStepIndex = -1;
    } else if (isAnalyzing) {
      currentStepIndex = 1; // Analyzing
    } else {
      // Map status to step index (accounting for analyzing step)
      const statusToStep: Record<string, number> = {
        'pending': 2,
        'in_progress': 3,
        'completed': 4,
        'cancelled': -1
      };
      currentStepIndex = statusToStep[status] ?? 0;
    }

    return (
      <View style={styles.timelineWrapper}>
        <View style={styles.timelineContainer}>
          {steps.map((step, index) => {
            const isActive = !isCancelled && index <= currentStepIndex;
            const isLastStep = index === steps.length - 1;
            
            return (
              <View key={step} style={styles.timelineStep}>
                <View 
                  style={[
                    styles.timelineDot,
                    isActive && styles.timelineDotActive,
                    isCancelled && styles.timelineDotCancelled
                  ]} 
                />
                {!isLastStep && (
                  <View 
                    style={[
                      styles.timelineLine,
                      isActive && index < currentStepIndex && styles.timelineLineActive,
                      isCancelled && styles.timelineLineCancelled
                    ]} 
                  />
                )}
              </View>
            );
          })}
          {isCancelled && (
            <View style={styles.cancelledIndicator}>
              <Text style={styles.cancelledText}>✕</Text>
            </View>
          )}
        </View>
        <View style={styles.timelineLabels}>
          <Text style={styles.timelineLabelActive}>
            {isCancelled ? 'Cancelled' : stepLabels[currentStepIndex]}
          </Text>
        </View>
      </View>
    );
  };

  if (loading) {
    return (
      <View style={styles.container}>
        <View style={styles.header}>
          <View style={styles.headerLeft}>
            <Wrench size={24} color="#111827" />
            <Text style={styles.title}>Service Requests</Text>
          </View>
        </View>
        <View style={styles.loadingContainer}>
          <ActivityIndicator size="small" color="#9ca3af" />
        </View>
      </View>
    );
  }

  return (
    <View style={styles.container}>
      <View style={styles.header}>
        <View style={styles.headerLeft}>
          <Wrench size={24} color="#111827" />
          <Text style={styles.title}>Service Requests</Text>
        </View>
        <Pressable
          onPress={() => navigate('/service-requests')}
          style={styles.viewAllButton}
        >
          <Text style={styles.viewAllText}>View All</Text>
          <ChevronRight size={16} color="#2563eb" />
        </Pressable>
      </View>

      {requests.length === 0 ? (
        <View style={styles.emptyState}>
          <Text style={styles.emptyText}>No service requests yet</Text>
          <Pressable
            onPress={() => navigate('/service-requests', { state: { openNewRequest: true } })}
            style={styles.createButton}
          >
            <Plus size={16} color="#ffffff" />
            <Text style={styles.createButtonText}>Create Request</Text>
          </Pressable>
        </View>
      ) : (
        <View style={styles.requestsList}>
          <Pressable
            onPress={() => navigate('/service-requests', { state: { openNewRequest: true } })}
            style={styles.submitRequestButton}
          >
            <Plus size={16} color="#ffffff" />
            <Text style={styles.submitRequestButtonText}>Request Service</Text>
          </Pressable>
          {requests.map((request) => {
            return (
              <Pressable
                key={request.id}
                onPress={() => {
                  setSelectedRequest(request);
                  setIsDetailOpen(true);
                  fetchNotes(request.id);
                  fetchQuotes(request.id);
                  fetchAttachments(request.id);
                }}
                style={({ pressed }) => [
                  styles.requestItem,
                  pressed && styles.requestItemPressed
                ]}
              >
                <View style={styles.requestContent}>
                  <View style={styles.requestMain}>
                    <Text style={styles.requestTitle} numberOfLines={1}>
                      {request.title}
                    </Text>
                    {renderProgressTimeline(request)}
                    <Text style={styles.requestDescription} numberOfLines={2}>
                      {request.description}
                    </Text>
                  </View>
                  <View style={styles.requestMeta}>
                    <Text style={styles.requestDate}>
                      {new Date(request.created_at).toLocaleDateString()}
                    </Text>
                    <ChevronRight size={20} color="#9ca3af" />
                  </View>
                </View>
              </Pressable>
            );
          })}
        </View>
      )}

      {/* Service Request Detail Bottom Sheet */}
      <BottomSheet
        isOpen={isDetailOpen}
        onClose={() => {
          setIsDetailOpen(false);
          setSelectedRequestNotes([]);
          setQuotes([]);
          setQuoteAttachments({});
          setServiceRequestAttachments([]);
        }}
        title="Service Request Details"
        height="full"
      >
        {selectedRequest && (
          <View style={styles.detailContent}>
            {/* Title */}
            <View style={styles.detailSection}>
              <Text style={styles.detailTitle}>{selectedRequest.title}</Text>
            </View>

            {/* Progress Timeline */}
            <View style={styles.detailSection}>
              <Text style={styles.detailLabel}>Status</Text>
              {renderProgressTimeline(selectedRequest)}
            </View>

            {/* Description */}
            <View style={styles.detailSection}>
              <Text style={styles.detailLabel}>Description</Text>
              <View style={styles.detailDescriptionBox}>
                <Text style={styles.detailText}>{selectedRequest.description}</Text>
              </View>
            </View>

            {/* Priority */}
            <View style={styles.detailSection}>
              <Text style={styles.detailLabel}>Priority</Text>
              <View style={styles.detailRow}>
                <AlertCircle size={16} color={getPriorityColor(selectedRequest.priority).text} />
                <View style={[styles.tag, { backgroundColor: getPriorityColor(selectedRequest.priority).bg }]}>
                  <Text style={[styles.tagText, { color: getPriorityColor(selectedRequest.priority).text }]}>
                    {selectedRequest.priority.charAt(0).toUpperCase() + selectedRequest.priority.slice(1)} Priority
                  </Text>
                </View>
              </View>
            </View>

            {/* Assigned To */}
            {selectedRequest.assigned_owner && (
              <View style={styles.detailSection}>
                <Text style={styles.detailLabel}>Assigned To</Text>
                <View style={styles.detailRow}>
                  <UserCheck size={16} color="#16a34a" />
                  <Text style={styles.detailText}>
                    {selectedRequest.assigned_owner.full_name}
                  </Text>
                </View>
              </View>
            )}

            {/* Visit Information */}
            {selectedRequest.visit_scheduled_at && (
              <View style={styles.detailSection}>
                <Text style={styles.detailLabel}>Visit Information</Text>
                <View style={styles.visitInfoBox}>
                  <View style={styles.detailRow}>
                    <Calendar size={16} color="#2563eb" />
                    <Text style={styles.visitInfoText}>
                      Scheduled: {new Date(selectedRequest.visit_scheduled_at).toLocaleDateString('en-US', {
                        weekday: 'short',
                        month: 'short',
                        day: 'numeric',
                        hour: 'numeric',
                        minute: '2-digit'
                      })}
                    </Text>
                  </View>
                  {selectedRequest.visit_completed_at && (
                    <View style={[styles.detailRow, { marginTop: 8 }]}>
                      <Clock size={16} color="#16a34a" />
                      <Text style={styles.visitCompletedText}>
                        Completed: {new Date(selectedRequest.visit_completed_at).toLocaleDateString('en-US', {
                          weekday: 'short',
                          month: 'short',
                          day: 'numeric'
                        })}
                      </Text>
                    </View>
                  )}
                </View>
              </View>
            )}

            {/* Location */}
            {selectedRequest.home && (
              <View style={styles.detailSection}>
                <Text style={styles.detailLabel}>Location</Text>
                <View style={styles.detailRow}>
                  <MapPin size={16} color="#6b7280" />
                  <Text style={styles.detailText}>
                    {selectedRequest.home.address}, {selectedRequest.home.city}, {selectedRequest.home.state}
                  </Text>
                </View>
              </View>
            )}

            {/* Created Date */}
            <View style={styles.detailSection}>
              <Text style={styles.detailLabel}>Created</Text>
              <View style={styles.detailRow}>
                <Calendar size={16} color="#6b7280" />
                <Text style={styles.detailText}>
                  {new Date(selectedRequest.created_at).toLocaleDateString('en-US', {
                    weekday: 'long',
                    year: 'numeric',
                    month: 'long',
                    day: 'numeric'
                  })}
                </Text>
              </View>
            </View>

            {/* Notes Section */}
            <View style={styles.detailSection}>
              <View style={styles.detailSectionHeader}>
                <MessageSquare size={18} color="#6b7280" />
                <Text style={styles.detailLabel}>Notes & Updates</Text>
              </View>
              {loadingNotes ? (
                <View style={styles.notesLoadingContainer}>
                  <ActivityIndicator size="small" color="#9ca3af" />
                </View>
              ) : selectedRequestNotes.length === 0 ? (
                <Text style={styles.noNotesText}>No notes or updates yet</Text>
              ) : (
                <View style={styles.notesList}>
                  {selectedRequestNotes.map((note) => (
                    <View key={note.id} style={styles.noteCard}>
                      <Text style={styles.noteText}>{note.content}</Text>
                      <View style={styles.noteFooter}>
                        <Text style={styles.noteDate}>
                          {new Date(note.created_at).toLocaleDateString()} at {new Date(note.created_at).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
                        </Text>
                        <Text style={styles.noteAuthor}>
                          {note.creator?.full_name || 'System'}
                        </Text>
                      </View>
                    </View>
                  ))}
                </View>
              )}
            </View>

            {/* Quotes Section */}
            <View style={styles.detailSection}>
              <View style={styles.detailSectionHeader}>
                <DollarSign size={18} color="#6b7280" />
                <Text style={styles.detailLabel}>Quotes/Estimates</Text>
              </View>
              {loadingQuotes ? (
                <View style={styles.notesLoadingContainer}>
                  <ActivityIndicator size="small" color="#9ca3af" />
                </View>
              ) : quotes.length === 0 ? (
                <Text style={styles.noNotesText}>No quotes yet</Text>
              ) : (
                <View style={styles.quotesList}>
                  {quotes.map((quote) => {
                    const quoteStatusColors = getQuoteStatusColor(quote.status);
                    return (
                      <View key={quote.id} style={styles.quoteCard}>
                        <View style={styles.quoteHeader}>
                          <View style={styles.quoteInfo}>
                            <Text style={styles.quoteProviderName}>
                              {quote.service_provider?.company_name || 'Unknown Provider'}
                            </Text>
                            <View style={[styles.tag, { backgroundColor: quoteStatusColors.bg }]}>
                              <Text style={[styles.tagText, { color: quoteStatusColors.text }]}>
                                {quote.status.charAt(0).toUpperCase() + quote.status.slice(1)}
                              </Text>
                            </View>
                          </View>
                          {quote.amount !== null && quote.amount !== undefined && (
                            <Text style={styles.quoteAmount}>{formatCurrency(quote.amount)}</Text>
                          )}
                        </View>
                        {quote.notes && (
                          <Text style={styles.quoteNotes}>{quote.notes}</Text>
                        )}
                        <View style={styles.quoteMeta}>
                          {quote.quote_date && (
                            <View style={styles.detailRow}>
                              <Calendar size={12} color="#6b7280" />
                              <Text style={styles.quoteMetaText}>
                                Quote Date: {new Date(quote.quote_date).toLocaleDateString()}
                              </Text>
                            </View>
                          )}
                          {quote.expires_at && (
                            <View style={styles.detailRow}>
                              <Clock size={12} color="#6b7280" />
                              <Text style={styles.quoteMetaText}>
                                Expires: {new Date(quote.expires_at).toLocaleDateString()}
                              </Text>
                            </View>
                          )}
                        </View>
                        {/* Quote Attachments */}
                        {quoteAttachments[quote.id] && quoteAttachments[quote.id].length > 0 && (
                          <View style={styles.attachmentsContainer}>
                            {quoteAttachments[quote.id].map((attachment) => (
                              <Pressable
                                key={attachment.id}
                                style={styles.attachmentItem}
                                onPress={() => downloadAttachment(attachment, 'service-request-quote-attachments')}
                              >
                                <View style={styles.attachmentContent}>
                                  {getFileIcon(attachment.file_type)}
                                  <View style={styles.attachmentInfo}>
                                    <Text style={styles.attachmentName} numberOfLines={1}>
                                      {attachment.file_name}
                                    </Text>
                                    <Text style={styles.attachmentSize}>
                                      {formatFileSize(attachment.file_size)}
                                    </Text>
                                  </View>
                                </View>
                                <View style={styles.attachmentActions}>
                                  {isImageFile(attachment.file_type) && (
                                    <Pressable
                                      style={styles.attachmentAction}
                                      onPress={(e) => {
                                        e.stopPropagation();
                                        viewImage(attachment, 'service-request-quote-attachments');
                                      }}
                                    >
                                      <Eye size={14} color="#6b7280" />
                                    </Pressable>
                                  )}
                                  <Pressable
                                    style={styles.attachmentAction}
                                    onPress={(e) => {
                                      e.stopPropagation();
                                      downloadAttachment(attachment, 'service-request-quote-attachments');
                                    }}
                                  >
                                    <Download size={14} color="#6b7280" />
                                  </Pressable>
                                </View>
                              </Pressable>
                            ))}
                          </View>
                        )}
                        <Text style={styles.quoteCreated}>
                          Created: {new Date(quote.created_at).toLocaleDateString()} at {new Date(quote.created_at).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
                        </Text>
                      </View>
                    );
                  })}
                </View>
              )}
            </View>

            {/* Attachments Section */}
            <View style={styles.detailSection}>
              <View style={styles.detailSectionHeader}>
                <Paperclip size={18} color="#6b7280" />
                <Text style={styles.detailLabel}>Attachments</Text>
              </View>
              {loadingAttachments ? (
                <View style={styles.notesLoadingContainer}>
                  <ActivityIndicator size="small" color="#9ca3af" />
                </View>
              ) : serviceRequestAttachments.length === 0 ? (
                <Text style={styles.noNotesText}>No attachments yet</Text>
              ) : (
                <View style={styles.attachmentsContainer}>
                  {serviceRequestAttachments.map((attachment) => (
                    <Pressable
                      key={attachment.id}
                      style={styles.attachmentItem}
                      onPress={() => downloadAttachment(attachment, 'service-request-attachments')}
                    >
                      <View style={styles.attachmentContent}>
                        {getFileIcon(attachment.file_type)}
                        <View style={styles.attachmentInfo}>
                          <Text style={styles.attachmentName} numberOfLines={1}>
                            {attachment.file_name}
                          </Text>
                          <Text style={styles.attachmentSize}>
                            {formatFileSize(attachment.file_size)}
                          </Text>
                        </View>
                      </View>
                      <View style={styles.attachmentActions}>
                        {isImageFile(attachment.file_type) && (
                          <Pressable
                            style={styles.attachmentAction}
                            onPress={(e) => {
                              e.stopPropagation();
                              viewImage(attachment, 'service-request-attachments');
                            }}
                          >
                            <Eye size={14} color="#6b7280" />
                          </Pressable>
                        )}
                        <Pressable
                          style={styles.attachmentAction}
                          onPress={(e) => {
                            e.stopPropagation();
                            downloadAttachment(attachment, 'service-request-attachments');
                          }}
                        >
                          <Download size={14} color="#6b7280" />
                        </Pressable>
                      </View>
                    </Pressable>
                  ))}
                </View>
              )}
            </View>
          </View>
        )}
      </BottomSheet>

      {/* Image Preview Modal */}
      <Modal
        visible={!!previewImage}
        transparent
        animationType="fade"
        onRequestClose={() => setPreviewImage(null)}
      >
        <TouchableWithoutFeedback onPress={() => setPreviewImage(null)}>
          <View style={styles.imagePreviewOverlay}>
            <Pressable
              style={styles.imagePreviewClose}
              onPress={() => setPreviewImage(null)}
            >
              <Text style={styles.imagePreviewCloseText}>✕</Text>
            </Pressable>
            {previewImage && (
              <Image
                source={{ uri: previewImage }}
                style={styles.imagePreview}
                resizeMode="contain"
              />
            )}
          </View>
        </TouchableWithoutFeedback>
      </Modal>
    </View>
  );
};

const styles = StyleSheet.create({
  container: {
    backgroundColor: '#ffffff',
    borderRadius: 12,
    borderWidth: 1,
    borderColor: '#f3f4f6',
    padding: 24,
    gap: 20,
    ...(Platform.OS === 'web' && {
      ...shadows.sm,
    }),
  },
  header: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
  },
  headerLeft: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 12,
  },
  title: {
    fontSize: 20,
    fontWeight: '600',
    color: '#111827',
  },
  viewAllButton: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 4,
  },
  viewAllText: {
    fontSize: 14,
    color: '#2563eb',
    fontWeight: '500',
  },
  loadingContainer: {
    padding: 32,
    alignItems: 'center',
    justifyContent: 'center',
  },
  emptyState: {
    padding: 32,
    alignItems: 'center',
    gap: 16,
  },
  emptyText: {
    fontSize: 14,
    color: '#6b7280',
  },
  createButton: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 8,
    paddingHorizontal: 16,
    paddingVertical: 8,
    backgroundColor: '#000000',
    borderRadius: 8,
  },
  createButtonText: {
    color: '#ffffff',
    fontSize: 14,
    fontWeight: '500',
  },
  requestsList: {
    gap: 12,
  },
  submitRequestButton: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'center',
    gap: 8,
    paddingHorizontal: 16,
    paddingVertical: 12,
    backgroundColor: '#000000',
    borderRadius: 8,
    marginBottom: 4,
  },
  submitRequestButtonText: {
    color: '#ffffff',
    fontSize: 15,
    fontWeight: '600',
  },
  requestItem: {
    borderRadius: 8,
    borderWidth: 1,
    borderColor: '#e5e7eb',
    backgroundColor: '#fafafa',
    ...(Platform.OS === 'web' && {
      cursor: 'pointer',
      transition: 'all 0.2s',
    }),
  },
  requestItemPressed: {
    backgroundColor: '#f3f4f6',
  },
  requestContent: {
    flexDirection: 'row',
    padding: 16,
    gap: 16,
  },
  requestMain: {
    flex: 1,
    gap: 8,
  },
  requestTitle: {
    fontSize: 16,
    fontWeight: '500',
    color: '#111827',
  },
  requestDescription: {
    fontSize: 14,
    color: '#6b7280',
    lineHeight: 20,
  },
  requestTags: {
    flexDirection: 'row',
    flexWrap: 'wrap',
    gap: 8,
  },
  tag: {
    paddingHorizontal: 8,
    paddingVertical: 4,
    borderRadius: 6,
  },
  tagText: {
    fontSize: 12,
    fontWeight: '500',
  },
  requestMeta: {
    alignItems: 'flex-end',
    justifyContent: 'space-between',
  },
  requestDate: {
    fontSize: 12,
    color: '#9ca3af',
  },
  timelineWrapper: {
    paddingVertical: 8,
  },
  timelineContainer: {
    flexDirection: 'row',
    alignItems: 'center',
    position: 'relative',
    marginBottom: 4,
  },
  timelineStep: {
    flexDirection: 'row',
    alignItems: 'center',
    flex: 1,
  },
  timelineDot: {
    width: 10,
    height: 10,
    borderRadius: 5,
    backgroundColor: '#e5e7eb',
    borderWidth: 2,
    borderColor: '#e5e7eb',
  },
  timelineDotActive: {
    backgroundColor: '#000000',
    borderColor: '#000000',
  },
  timelineDotCancelled: {
    backgroundColor: '#e5e7eb',
    borderColor: '#9ca3af',
  },
  timelineLine: {
    flex: 1,
    height: 2,
    backgroundColor: '#e5e7eb',
    marginHorizontal: 4,
  },
  timelineLineActive: {
    backgroundColor: '#000000',
  },
  timelineLineCancelled: {
    backgroundColor: '#e5e7eb',
  },
  cancelledIndicator: {
    position: 'absolute',
    right: 0,
    width: 20,
    height: 20,
    borderRadius: 10,
    backgroundColor: '#dc2626',
    alignItems: 'center',
    justifyContent: 'center',
  },
  cancelledText: {
    color: '#ffffff',
    fontSize: 12,
    fontWeight: '600',
  },
  timelineLabels: {
    alignItems: 'flex-start',
  },
  timelineLabelActive: {
    fontSize: 10,
    color: '#6b7280',
    fontWeight: '500',
  },
  detailContent: {
    gap: 24,
  },
  detailSection: {
    gap: 8,
  },
  detailTitle: {
    fontSize: 24,
    fontWeight: '600',
    color: '#111827',
    lineHeight: 32,
  },
  detailLabel: {
    fontSize: 12,
    fontWeight: '600',
    color: '#6b7280',
    textTransform: 'uppercase',
    letterSpacing: 0.5,
  },
  detailText: {
    fontSize: 15,
    color: '#111827',
    lineHeight: 22,
  },
  detailDescriptionBox: {
    backgroundColor: '#f9fafb',
    padding: 12,
    borderRadius: 8,
  },
  detailRow: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 8,
    flexWrap: 'wrap',
  },
  detailSectionHeader: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 8,
    marginBottom: 4,
  },
  visitInfoBox: {
    backgroundColor: '#eff6ff',
    padding: 12,
    borderRadius: 8,
    gap: 4,
  },
  visitInfoText: {
    fontSize: 14,
    fontWeight: '500',
    color: '#2563eb',
    flex: 1,
  },
  visitCompletedText: {
    fontSize: 14,
    fontWeight: '500',
    color: '#16a34a',
    flex: 1,
  },
  notesLoadingContainer: {
    padding: 16,
    alignItems: 'center',
    justifyContent: 'center',
  },
  noNotesText: {
    fontSize: 14,
    color: '#9ca3af',
    fontStyle: 'italic',
  },
  notesList: {
    gap: 12,
  },
  noteCard: {
    backgroundColor: '#f9fafb',
    padding: 12,
    borderRadius: 8,
    borderWidth: 1,
    borderColor: '#e5e7eb',
  },
  noteText: {
    fontSize: 14,
    color: '#374151',
    lineHeight: 20,
    marginBottom: 8,
  },
  noteFooter: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
  },
  noteDate: {
    fontSize: 12,
    color: '#9ca3af',
  },
  noteAuthor: {
    fontSize: 12,
    color: '#6b7280',
    fontWeight: '500',
  },
  quotesList: {
    gap: 12,
  },
  quoteCard: {
    backgroundColor: '#f9fafb',
    padding: 12,
    borderRadius: 8,
    borderWidth: 1,
    borderColor: '#e5e7eb',
    gap: 8,
  },
  quoteHeader: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'flex-start',
    gap: 12,
  },
  quoteInfo: {
    flex: 1,
    flexDirection: 'row',
    alignItems: 'center',
    gap: 8,
    flexWrap: 'wrap',
  },
  quoteProviderName: {
    fontSize: 15,
    fontWeight: '500',
    color: '#111827',
  },
  quoteAmount: {
    fontSize: 18,
    fontWeight: '600',
    color: '#111827',
  },
  quoteNotes: {
    fontSize: 14,
    color: '#374151',
    lineHeight: 20,
  },
  quoteMeta: {
    flexDirection: 'row',
    flexWrap: 'wrap',
    gap: 12,
  },
  quoteMetaText: {
    fontSize: 12,
    color: '#6b7280',
  },
  quoteCreated: {
    fontSize: 11,
    color: '#9ca3af',
    marginTop: 4,
  },
  attachmentsContainer: {
    gap: 8,
    marginTop: 8,
  },
  attachmentItem: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    backgroundColor: '#ffffff',
    padding: 10,
    borderRadius: 6,
    borderWidth: 1,
    borderColor: '#e5e7eb',
    ...(Platform.OS === 'web' && {
      cursor: 'pointer',
    }),
  },
  attachmentContent: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 8,
    flex: 1,
  },
  attachmentInfo: {
    flex: 1,
    gap: 2,
  },
  attachmentName: {
    fontSize: 13,
    color: '#111827',
    fontWeight: '500',
  },
  attachmentSize: {
    fontSize: 11,
    color: '#6b7280',
  },
  attachmentActions: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 8,
  },
  attachmentAction: {
    padding: 4,
  },
  imagePreviewOverlay: {
    flex: 1,
    backgroundColor: 'rgba(0, 0, 0, 0.9)',
    justifyContent: 'center',
    alignItems: 'center',
  },
  imagePreviewClose: {
    position: 'absolute',
    top: 20,
    right: 20,
    zIndex: 1,
    width: 40,
    height: 40,
    borderRadius: 20,
    backgroundColor: 'rgba(255, 255, 255, 0.2)',
    justifyContent: 'center',
    alignItems: 'center',
  },
  imagePreviewCloseText: {
    color: '#ffffff',
    fontSize: 24,
    fontWeight: '600',
  },
  imagePreview: {
    width: '90%',
    height: '90%',
  },
});

export default ServiceRequestHistory;

